const CURRENCY_SCALE_SUFFIX =
  '(?:\\s*(?:million|mil|bn|billion|thousand)|\\s*m(?=\\s|$|[^a-zA-Z])|\\s*k(?=\\s|$|[^a-zA-Z]))'

const CURRENCY_AMOUNT = '\\d+(?:,\\d{3})*(?:\\.\\d{1,2})?'

const LIMIT_VALUE_SOURCE =
  `(?:\\$|£)\\s?${CURRENCY_AMOUNT}${CURRENCY_SCALE_SUFFIX}?|\\d+(?:\\.\\d+)?\\s*%`

export const LIMIT_VALUE_PATTERN = new RegExp(LIMIT_VALUE_SOURCE, 'gi')

// Non-global copy for .test() — a 'g' regex keeps lastIndex state across calls.
const LIMIT_VALUE_TEST = new RegExp(LIMIT_VALUE_SOURCE, 'i')

// Icon codes ComparisonIcon renders as Limit / Sub Limit / Limited.
const LIMIT_ICON_CODES = new Set(['lt*', 'lta*', 'sl', 'sla', 'ltd*'])

export function isLimitIcon(icon: string | null | undefined): boolean {
  return !!icon && LIMIT_ICON_CODES.has(icon.toLowerCase())
}

interface LimitMatch {
  start: number
  end: number
  value: string
}

function normalizeLimitValue(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(\d)\s+%/, '$1%')
    .replace(/[,.]$/, '')
}

function findLimitMatches(comment: string): LimitMatch[] {
  const matches: LimitMatch[] = []
  const pattern = new RegExp(LIMIT_VALUE_SOURCE, 'gi')
  let match: RegExpExecArray | null

  while ((match = pattern.exec(comment)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      value: normalizeLimitValue(match[0]),
    })
  }

  return matches
}

export function comparisonCommentHasLimitValues(comment: string | null | undefined): boolean {
  if (!comment) return false
  return LIMIT_VALUE_TEST.test(comment)
}

export function extractLimitValues(comment: string | null | undefined): string[] {
  if (!comment) return []
  return [...new Set(findLimitMatches(comment).map((match) => match.value))]
}

export interface CommentLimitSegment {
  type: 'text' | 'limit'
  value: string
}

// Comments are static per comparison, so segments can be cached by comment text.
// Callers must treat the returned array as read-only.
const splitCache = new Map<string, CommentLimitSegment[]>()
const SPLIT_CACHE_MAX = 2000

export function splitCommentByLimits(comment: string): CommentLimitSegment[] {
  if (!comment) return []

  const cached = splitCache.get(comment)
  if (cached) return cached

  const segments = computeCommentSegments(comment)
  if (splitCache.size >= SPLIT_CACHE_MAX) splitCache.clear()
  splitCache.set(comment, segments)
  return segments
}

function computeCommentSegments(comment: string): CommentLimitSegment[] {
  const matches = findLimitMatches(comment)
  if (!matches.length) return [{ type: 'text', value: comment }]

  const segments: CommentLimitSegment[] = []
  let lastIndex = 0

  matches.forEach((match) => {
    if (match.start > lastIndex) {
      segments.push({ type: 'text', value: comment.slice(lastIndex, match.start) })
    }
    segments.push({ type: 'limit', value: match.value })
    lastIndex = match.end
  })

  if (lastIndex < comment.length) {
    segments.push({ type: 'text', value: comment.slice(lastIndex) })
  }

  return segments
}
